'use client';

import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress } from '@mui/material';
import { fetchAuthSession } from '@parkchirp/api-client';
import {
	type CreateJobResponse,
	isClosedJobStatus,
	type JobResponse,
	type SubscriptionExportFilter,
} from '@parkchirp/reporting';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SetShowSnackbar } from '../../types';
import { useCreateReport } from '../reporting/useCreateReport';
import { useReportJobPolling } from '../reporting/useReportJobPolling';

type Props = {
	filters: SubscriptionExportFilter[];
	order: { field: string; sort: 'asc' | 'desc' };
	facilityId?: string | null;
	setShowSnackbar?: SetShowSnackbar;
};

const downloadFile = (url: string, fileName: string) => {
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	link.remove();
};

const jobIsRunning = (createdJob: CreateJobResponse | null, job: JobResponse | null) => {
	if (!createdJob) {
		return false;
	}
	if (job?.jobId === createdJob.jobId) {
		return !isClosedJobStatus(job.status);
	}
	return !isClosedJobStatus(createdJob.status);
};

const CLOSED_STATUS_SNACKBAR_DURATION_MS = 6_000;

export const SubscriptionReportExportButton = ({ filters, order, facilityId, setShowSnackbar }: Props) => {
	const [authToken, setAuthToken] = useState('');
	const baseUrl = process.env.NEXT_PUBLIC_REPORTING_API_URL ?? '';
	const lastHandledJobIdRef = useRef<string | null>(null);

	const {
		clearCreatedJob,
		createReport,
		createdJob,
		error: createError,
		isCreatingReport,
	} = useCreateReport({
		authToken,
		baseUrl,
	});

	const { job, error: pollingError } = useReportJobPolling({
		authToken,
		baseUrl,
		jobId: createdJob?.jobId,
	});

	const isDownloading = useMemo(
		() => isCreatingReport || jobIsRunning(createdJob, job),
		[createdJob, isCreatingReport, job],
	);
	const currentJobStatus = job && createdJob && job.jobId === createdJob.jobId ? job.status : createdJob?.status;

	useEffect(() => {
		let active = true;

		fetchAuthSession()
			.then(({ tokens }) => {
				if (active) {
					setAuthToken(tokens?.idToken?.toString() ?? '');
				}
			})
			.catch(() => {
				if (active) {
					setAuthToken('');
				}
			});

		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		const message = createError ?? pollingError;
		if (!message) {
			return;
		}

		setShowSnackbar?.({ message, severity: 'error', autoHideDuration: CLOSED_STATUS_SNACKBAR_DURATION_MS });
		if (pollingError) {
			clearCreatedJob();
		}
	}, [clearCreatedJob, createError, pollingError, setShowSnackbar]);

	useEffect(() => {
		if (currentJobStatus === 'queued') {
			setShowSnackbar?.({
				message: 'Your subscription export is queued.',
				severity: 'info',
				autoHideDuration: null,
			});
		} else if (currentJobStatus === 'running') {
			setShowSnackbar?.({
				message: 'Your subscription export is being generated.',
				severity: 'info',
				autoHideDuration: null,
			});
		}
	}, [currentJobStatus, setShowSnackbar]);

	useEffect(() => {
		if (!job || !createdJob || job.jobId !== createdJob.jobId || !isClosedJobStatus(job.status)) {
			return;
		}
		if (lastHandledJobIdRef.current === job.jobId) {
			return;
		}

		lastHandledJobIdRef.current = job.jobId;

		if (job.status === 'completed') {
			if (job.output) {
				downloadFile(job.output.downloadUrl, job.output.fileName);
				setShowSnackbar?.({
					message: 'Your subscription export is ready. The download has started.',
					severity: 'success',
					autoHideDuration: CLOSED_STATUS_SNACKBAR_DURATION_MS,
				});
			} else {
				setShowSnackbar?.({
					message: 'The subscription export completed without a download file.',
					severity: 'error',
					autoHideDuration: CLOSED_STATUS_SNACKBAR_DURATION_MS,
				});
			}
		} else if (job.status === 'noData') {
			setShowSnackbar?.({
				message: 'No subscription records matched the current filters.',
				severity: 'warning',
				autoHideDuration: CLOSED_STATUS_SNACKBAR_DURATION_MS,
			});
		} else if (job.status === 'failed') {
			setShowSnackbar?.({
				message: job.error?.message ?? 'Failed to generate the subscription export.',
				severity: 'error',
				autoHideDuration: CLOSED_STATUS_SNACKBAR_DURATION_MS,
			});
		} else if (job.status === 'cancelled') {
			setShowSnackbar?.({
				message: 'The subscription export was cancelled.',
				severity: 'warning',
				autoHideDuration: CLOSED_STATUS_SNACKBAR_DURATION_MS,
			});
		}

		clearCreatedJob();
	}, [clearCreatedJob, createdJob, job, setShowSnackbar]);

	const handleDownload = useCallback(async () => {
		if (!authToken || !baseUrl || isDownloading) {
			return;
		}

		lastHandledJobIdRef.current = null;
		setShowSnackbar?.({
			message: 'Starting your subscription export.',
			severity: 'info',
			autoHideDuration: null,
		});

		await createReport({
			reportType: 'subscriptionExport',
			parameters: {
				filters,
				order,
				...(facilityId ? { facilityId } : {}),
			},
		});
	}, [authToken, baseUrl, createReport, facilityId, filters, isDownloading, order, setShowSnackbar]);

	return (
		<Button
			size='small'
			startIcon={<DownloadIcon />}
			onClick={handleDownload}
			disabled={!authToken || !baseUrl || isDownloading}
		>
			<span style={{ marginRight: '5px' }}>Export</span>
			{isDownloading && <CircularProgress style={{ height: '20px', width: '20px' }} />}
		</Button>
	);
};
